import { useEffect, useMemo, useState } from 'react';

import { useFeatureFlag } from '~hooks';

import { useUpsertTablePreferencesMutation } from './userTablePreferencesActions';

import { ColumnSpec } from '~types/columnSpec';

const FEATURE_FLAG = 'feature-column-preferences';

interface UseColumnVisibilityParams {
  /** Identifies this table's saved preference row — must match the backend's table_key. */
  tableKey: string;
  /** The table's `specs=true` column metadata, already resolved per-user by the backend. */
  columnSpecs?: ColumnSpec[];
  /** Called once a toggle has been persisted, so the caller can refetch the specs it owns. */
  onPreferencesChanged?: () => void;
  /** Called when persisting a toggle fails, so the caller can surface it (e.g. a toast) — the
   * hook itself rolls back the optimistic override either way, but has no UI of its own to show
   * the failure with. */
  onError?: (error: unknown) => void;
}

interface UseColumnVisibilityResult {
  /** Whether the column-picker capability is enabled for this user. */
  isEnabled: boolean;
  /** Whether the feature flag check has settled — guards against a flash of the wrong state. */
  isResolved: boolean;
  /** The columns that should currently be shown. */
  visibleColumnIds: string[];
  /** Replaces the full visible-column selection and persists it. */
  setVisibleColumnIds: (visibleColumnIds: string[]) => void;
}

const sameIds = (a: string[], b: string[]): boolean => a.length === b.length && a.every((id) => b.includes(id));

/**
 * Reads a table's already-resolved column visibility (the backend merges each column's saved
 * preference, or its own defaultVisible, onto the specs response) and offers a picker + toggle.
 * Generic across tables — any table with a `specs=true` contract and a `tableKey` can reuse this.
 */
export const useColumnVisibility = ({
  tableKey,
  columnSpecs,
  onPreferencesChanged,
  onError,
}: UseColumnVisibilityParams): UseColumnVisibilityResult => {
  const { isEnabled: isFlagEnabled, isResolved } = useFeatureFlag(FEATURE_FLAG);
  const isEnabled = isResolved && isFlagEnabled;

  const [upsertTablePreferences] = useUpsertTablePreferencesMutation();

  // Local, optimistic override for the brief window between a toggle and the follow-up specs
  // refetch landing — the backend response is the source of truth, this just avoids a wait before
  // the toggle feels like it did anything.
  const [pendingVisibleColumnIds, setPendingVisibleColumnIds] = useState<string[] | null>(null);

  const resolvedVisibleColumnIds = useMemo(
    () => (columnSpecs ?? []).filter((column) => column.visible).map((column) => column.key),
    [columnSpecs],
  );

  // Once the refetched specs actually reflect the pending toggle, stop overriding them, so a
  // later toggle (or an out-of-band change from another tab) flows through normally again.
  useEffect(() => {
    if (pendingVisibleColumnIds && sameIds(pendingVisibleColumnIds, resolvedVisibleColumnIds)) {
      setPendingVisibleColumnIds(null);
    }
  }, [pendingVisibleColumnIds, resolvedVisibleColumnIds]);

  const visibleColumnIds = pendingVisibleColumnIds ?? resolvedVisibleColumnIds;

  const setVisibleColumnIds = async (nextVisibleColumnIds: string[]) => {
    setPendingVisibleColumnIds(nextVisibleColumnIds);
    try {
      await upsertTablePreferences({ tableKey, visibleColumnIds: nextVisibleColumnIds }).unwrap();
      onPreferencesChanged?.();
    } catch (error) {
      setPendingVisibleColumnIds(null);
      onError?.(error);
    }
  };

  return { isEnabled, isResolved, visibleColumnIds, setVisibleColumnIds };
};
